import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { devError } from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches React render errors and shows a fallback UI instead of a blank screen.
 * Logs errors for debugging. Use around the main app content in App.tsx.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    devError('ErrorBoundary caught an error:', error, errorInfo.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-50" role="alert">
          <div className="max-w-md w-full text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" aria-hidden />
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Noe gikk galt</h1>
            <p className="text-sm text-gray-600 mb-4">
              En uventet feil oppstod. Du kan prøve å laste siden på nytt.
            </p>
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              aria-label="Last siden på nytt"
            >
              <RefreshCw className="w-4 h-4" aria-hidden />
              Last siden på nytt
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
